import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { forgotPassword, getLoggedInUser, login, logout, mfaLogin, register, resetPassword, updatePassword } from '../API/authApi';
import { attachAuthInterceptor, ejectAuthInterceptor } from '../axios/checkmateApi';
import type { RootState } from '../store/store';
import { IUser } from './userSlice';
import Cookies from 'js-cookie';
// CHKM-4023 round 2 (Item 5): imported from the leaf module `appActions.ts`,
// not from `../store/store`, so this file no longer creates a value-level
// import edge back into `store.ts` (which imports this file's default export
// as its `auth` reducer) — see appActions.ts for the full explanation.
import { resetAppState } from '../store/appActions';
import { clearMsalCache } from '../services/msalService';
export interface LoginPayload {
    email: string,
    password: string
}

export interface ForgotPayload {
    email: string
}

export interface ResetPasswordPayload {
    password: string,
    passwordConfirm: string,
    idToken: string
}

export interface RegisterPayload {
    email: string,
    password: string,
    confirmPassword: string
}

export interface PasswordPayload {
    currentPassword: string,
    password: string,
    confirmPassword: string
}

interface AuthState {
    authToken: string;
    loggedInUser: IUser | null;
    loggedOutUser: IUser | null;
}

// Define the initial state using that type
const initialState: AuthState = {
    authToken: Cookies.get('token') || '',
    loggedInUser: null,
    loggedOutUser: null,
};

export const callLogin = createAsyncThunk<any, LoginPayload>(
    'auth/login',
    async (payload, { dispatch }) => {
        const response = await login(payload);
        dispatch(setAuthToken(response.data.JWTToken))
        return response.data;
    }
);

export const callMFALogin = createAsyncThunk<any, string>(
    'auth/mfaLogin',
    async (token, { dispatch }) => {
        const response = await mfaLogin(token);
        dispatch(setAuthToken(response.data.JWTToken))
        return response.data;
    }
)

export const callGetLoggedInUser = createAsyncThunk<any, undefined>(
    'auth/loggedInUser',
    async (args, { dispatch }) => {
        const response = await getLoggedInUser();
        dispatch(setLoggedInUser(response.data));
        return response.data;
    }
)

export const callLogout = createAsyncThunk(
    'auth/logout',
    async (args, { dispatch, getState }) => {
        const state = getState() as RootState; // Get the current state
        const currentUser = state.auth.loggedInUser; // Store the current logged in user
        
        // Clear MSAL cache and accounts before clearing app state
        try {
            await clearMsalCache();
        } catch (error) {
            console.error('Error clearing MSAL cache on logout:', error);
            // Continue with logout even if MSAL cleanup fails
        }
        
        dispatch(resetAppState());
        dispatch(clearAuthToken());
        dispatch(setLoggedOutUser(currentUser)); // Set the logged out user
        return true;
    }
);

export const callRegister = createAsyncThunk<any, RegisterPayload>(
    'auth/register',
    async (payload, { dispatch }) => {
        const response = await register(payload);
        return response.data;
    }
)

export const callForgot = createAsyncThunk<any, ForgotPayload>(
    'auth/forgot',
    async (payload, { dispatch }) => {
        const response = await forgotPassword(payload);
        return response.data;
    }
)

export const callResetPassword = createAsyncThunk<any, ResetPasswordPayload>(
    'auth/resetPW',
    async (payload, { dispatch }) => {
        const response = await resetPassword(payload);
        return response.data;
    }
)

export const callUpdatePassword = createAsyncThunk<any, { password: PasswordPayload, user: IUser }>(
    'auth/updatePassword',
    async (payload, { dispatch }) => {
        const email = payload.user.email;
        const response = await updatePassword(payload.password, email);
        return response.data;
    }
)

export const authSlice = createSlice({
  name: 'auth',
  // `createSlice` will infer the state type from the `initialState` argument
  initialState,
  reducers: {
      setAuthToken: (state, action: PayloadAction<string>) => {
          Cookies.set('token', action.payload, { expires: 7, secure: true });
          state.authToken = action.payload;
      },
      clearAuthToken: (state) => {
          state.loggedOutUser = state.loggedInUser;
          Cookies.remove('token');
          ejectAuthInterceptor();
          state.authToken = ''
      },
      setLoggedInUser: (state, action: PayloadAction<IUser>) => {
          state.loggedInUser = action.payload;
      },
      setLoggedOutUser: (state, action: PayloadAction<IUser | null>) => {
          state.loggedOutUser = action.payload;
      }
  }
});

export const {
    setAuthToken,
    clearAuthToken,
    setLoggedInUser,
    setLoggedOutUser
} = authSlice.actions;

export const selectIsUserLoggedIn = (state: RootState): boolean => !!state.auth.authToken;
export const selectAuthToken = (state: RootState): string => state.auth.authToken;
export const selectLoggedInUser = (state: RootState): IUser | null => state.auth.loggedInUser;
export const selectIsAdmin = (state: RootState): boolean => state.auth.loggedInUser?.isAdmin || false;
export const selectLoggedInUserId = (state: RootState): string => state.auth.loggedInUser?.id || '';
export const selectLoggedOutUser = (state: RootState): IUser | null => state.auth.loggedOutUser;

export default authSlice.reducer;
